/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, Stack, TextField, Tooltip } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";

import { Effect, Material, Nullable, StorageLocation } from "../../utils/Interfaces";
import Autocomplete from "@mui/material/Autocomplete";
import { useAppDispatch } from "../../app/hooks";
import { Send } from "@mui/icons-material";

import { NumericFormat } from "react-number-format";
import { inventoryTransfer } from "../materials-transfers/materialTransferFunctions";

export interface ExportRowProps {
    amount: number;
    permanentLocations: StorageLocation[];
    sourceLocation: StorageLocation;
    material: Material;
    onTransferSuccess: Effect;
    onTransferFailure: Effect;
}

const TransferRow = (props: ExportRowProps) => {
    const { amount, permanentLocations, sourceLocation, material, onTransferSuccess, onTransferFailure } = props;

    const isRestock: boolean = amount >= 0;
    const amountAbs: number = Math.abs(amount);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [selectedPermanentStorageLocation, setSelectedPermanentStorageLocation] = useState<Nullable<StorageLocation>>(null);
    const [amountState, setAmountState] = useState<number>(amountAbs);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setSelectedPermanentStorageLocation(permanentLocations.length > 0 ? permanentLocations[0] : null);
    }, [permanentLocations]);

    return (
        <Stack direction="row" justifyContent="flex-start">
            <Autocomplete
                disablePortal
                onChange={(_event, value) => {
                    setSelectedPermanentStorageLocation(
                        value ? value : permanentLocations.length > 0 ? permanentLocations[0] : null
                    );
                }}
                size="small"
                id="storageLocationLookup"
                getOptionKey={(option: StorageLocation) => option.id}
                options={permanentLocations}
                value={selectedPermanentStorageLocation}
                getOptionLabel={(option: StorageLocation) => option.name}
                renderInput={(params) => (
                    <TextField
                        sx={{ mt: 0.5, width: 300 }}
                        required
                        {...params}
                        label={isRestock ? "From Inventory" : "To Inventory"}
                    />
                )}
            />

            <NumericFormat
                allowNegative={false}
                isAllowed={(values) => (values.floatValue ?? 1) <= 100}
                value={amount}
                customInput={TextField}
                style={{ marginLeft: 10, marginTop: 4, width: "100%" }}
                label="Amount to Transfer"
                thousandSeparator=","
                decimalScale={0}
                prefix=""
                max={10}
                size="small"
                onValueChange={(values) => setAmountState(values.floatValue ?? 1)}
            />

            {loading && (
                <Box>
                    <CircularProgress size={16} sx={{ ml: 2, mr: 2, mt: 1.75 }} />
                </Box>
            )}

            {!loading && (
                <Box sx={{ ml: 1, mt: 0.25 }}>
                    <Tooltip title={`Transfer ${amountAbs}.`}>
                        <IconButton
                            onClick={() => {
                                if (selectedPermanentStorageLocation === null) {
                                    return;
                                }

                                setLoading(true);

                                inventoryTransfer(
                                    isRestock ? (selectedPermanentStorageLocation as StorageLocation) : sourceLocation,
                                    isRestock ? sourceLocation : (selectedPermanentStorageLocation as StorageLocation),
                                    material,
                                    amountState,
                                    "Technician Restocking automated transfer.",
                                    dispatch,
                                    () => {
                                        onTransferSuccess();
                                        setLoading(false);
                                    },
                                    () => {
                                        onTransferFailure();
                                        setLoading(false);
                                    }
                                );
                            }}
                        >
                            <Send />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Stack>
    );
};

export default TransferRow;
